import { createTheme } from '@mui/material/styles';
import { designTokens } from './designTokens';

/**
 * Enhanced Material-UI theme with design tokens integration
 * Provides consistent colours, typography, and spacing across the application
 */

const { colours, spacing, borderRadius, typography, shadows } = designTokens;

export const theme = createTheme({
  palette: {
    mode: 'light',
    
    // Primary brand colours
    primary: {
      25: '#F0F8FF',                    // Ultra-light blue for section backgrounds
      light: colours.primary[300],
      main: colours.primary[500],
      dark: colours.primary[700],
      50: colours.primary[50],          // Keep existing 50 shade for compatibility
      contrastText: '#FFFFFF'
    },
    
    // Secondary accent colours
    secondary: {
      light: colours.secondary[300],
      main: colours.secondary[500],
      dark: colours.secondary[700],
      contrastText: '#FFFFFF'
    },
    
    // Semantic colours
    success: {
      light: colours.semantic.success.light,
      main: colours.semantic.success.main,
      dark: colours.semantic.success.dark,
      contrastText: '#FFFFFF'
    },
    
    warning: {
      light: colours.semantic.warning.light,
      main: colours.semantic.warning.main,
      dark: colours.semantic.warning.dark,
      contrastText: '#FFFFFF'
    },
    
    error: {
      light: colours.semantic.error.light,
      main: colours.semantic.error.main,
      dark: colours.semantic.error.dark,
      contrastText: '#FFFFFF'
    },
    
    info: {
      light: colours.semantic.info.light,
      main: colours.semantic.info.main,
      dark: colours.semantic.info.dark,
      contrastText: '#FFFFFF'
    },
    
    // Neutral greys
    grey: {
      25: '#FAFAFA',                    // Ultra-light grey for section backgrounds
      50: colours.neutral[50],
      100: colours.neutral[100],
      200: colours.neutral[200],
      300: colours.neutral[300],
      400: colours.neutral[400],
      500: colours.neutral[500],
      600: colours.neutral[600],
      700: colours.neutral[700],
      800: colours.neutral[800],
      900: colours.neutral[900]
    },
    
    // Text colours (WCAG compliant)
    text: {
      primary: colours.neutral[900],     // 16.1:1 ratio
      secondary: colours.neutral[700],   // 6.24:1 ratio
      disabled: colours.neutral[500]     // 2.85:1 ratio (decorative only)
    },
    
    // Background colours
    background: {
      default: colours.neutral[0],       // Pure white
      paper: colours.neutral[0],         // Pure white
      subtle: colours.neutral[50]        // Very light grey
    },
    
    // Dividers and borders
    divider: colours.neutral[300],       // 1.21:1 (subtle borders)
    
    // Action colours (buttons, links, etc.)
    action: {
      active: colours.primary[500],      // 4.76:1 (WCAG AA)
      hover: colours.neutral[100],       // Light hover background
      selected: colours.primary[50],     // Very light selection
      disabled: colours.neutral[400],    // Disabled state
      disabledBackground: colours.neutral[200]
    }
  },
  
  typography: {
    fontFamily: '"MatterSQ", "Helvetica", "Arial", sans-serif',
    
    // Heading scales
    h1: {
      fontSize: typography['5xl'],
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em'
    },
    h2: {
      fontSize: typography['4xl'],
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em'
    },
    h3: {
      fontSize: typography['3xl'],
      fontWeight: 600,
      lineHeight: 1.3
    },
    h4: {
      fontSize: typography['2xl'],
      fontWeight: 600,
      lineHeight: 1.4
    },
    h5: {
      fontSize: typography.xl,
      fontWeight: 600,
      lineHeight: 1.4
    },
    h6: {
      fontSize: typography.lg,
      fontWeight: 600,
      lineHeight: 1.4
    },
    
    // Body text
    body1: {
      fontSize: typography.base,
      lineHeight: 1.6,
      fontWeight: 400
    },
    body2: {
      fontSize: typography.sm,
      lineHeight: 1.5,
      fontWeight: 400
    },
    
    // Utility text
    caption: {
      fontSize: typography.xs,
      lineHeight: 1.4,
      fontWeight: 400,
      color: colours.neutral[600]
    },
    overline: {
      fontSize: typography.xs,
      lineHeight: 1.4,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: colours.neutral[600]
    },
    
    // Button text
    button: {
      fontSize: typography.base,
      fontWeight: 600,
      textTransform: 'none', // Remove MUI default uppercase
      letterSpacing: '0.01em'
    }
  },
  
  spacing: (factor) => {
    // Convert MUI spacing to our design tokens
    const spaceValues = {
      0: '0px',
      1: spacing.xs,    // 4px
      2: spacing.sm,    // 8px
      3: spacing.md,    // 16px
      4: spacing.lg,    // 24px
      5: spacing.xl,    // 32px
      6: spacing.xxl,   // 48px
      7: spacing.xxxl   // 64px
    };
    return spaceValues[factor] || `${factor * 8}px`;
  },
  
  shape: {
    borderRadius: parseInt(borderRadius.md) // 8px default
  },
  
  shadows: [
    'none',
    shadows.sm,  // elevation 1
    shadows.sm,  // elevation 2
    shadows.md,  // elevation 3
    shadows.md,  // elevation 4
    shadows.lg,  // elevation 5
    shadows.lg,  // elevation 6
    shadows.xl,  // elevation 7
    shadows.xl,  // elevation 8
    shadows.xl,  // elevation 9
    shadows.xl,  // elevation 10
    shadows.xl,  // elevation 11
    shadows.xl,  // elevation 12
    shadows.xl,  // elevation 13
    shadows.xl,  // elevation 14
    shadows.xl,  // elevation 15
    shadows.xl,  // elevation 16
    shadows.xl,  // elevation 17
    shadows.xl,  // elevation 18
    shadows.xl,  // elevation 19
    shadows.xl,  // elevation 20
    shadows.xl,  // elevation 21
    shadows.xl,  // elevation 22
    shadows.xl,  // elevation 23
    shadows.xl   // elevation 24
  ],
  
  components: {
    // Button component overrides
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          textTransform: 'none',
          fontWeight: 600,
          padding: `${spacing.sm} ${spacing.lg}`,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: shadows.sm
          }
        },
        containedPrimary: {
          backgroundColor: colours.primary[500],
          '&:hover': {
            backgroundColor: colours.primary[600]
          }
        },
        containedSecondary: {
          backgroundColor: colours.secondary[500],
          '&:hover': {
            backgroundColor: colours.secondary[600]
          }
        },
        outlinedPrimary: {
          borderColor: colours.primary[500],
          color: colours.primary[500],
          '&:hover': {
            borderColor: colours.primary[600],
            backgroundColor: colours.primary[50]
          }
        }
      }
    },
    
    // Paper component overrides
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          border: `1px solid ${colours.neutral[200]}`
        },
        elevation0: {
          boxShadow: 'none'
        },
        elevation1: {
          boxShadow: shadows.sm
        }
      }
    },
    
    // Chip component overrides
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xl,
          fontWeight: 500
        }
      }
    },
    
    // TextField component overrides
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.md,
            '& fieldset': {
              borderColor: colours.neutral[300]
            },
            '&:hover fieldset': {
              borderColor: colours.neutral[400]
            },
            '&.Mui-focused fieldset': {
              borderColor: colours.primary[500]
            }
          }
        }
      }
    }
  }
});

// Dark mode theme (for future implementation)
export const darkTheme = createTheme({
  ...theme,
  palette: {
    ...theme.palette,
    mode: 'dark',
    background: {
      default: colours.neutral[900],
      paper: colours.neutral[800],
      subtle: colours.neutral[800]
    },
    text: {
      primary: colours.neutral[50],
      secondary: colours.neutral[300],
      disabled: colours.neutral[500]
    },
    divider: colours.neutral[700]
  }
});

export default theme;